import React, {useCallback, useEffect, useRef} from 'react';
import I18n from 'app/i18n';
import {getLinkPath} from 'app/utils';
import {
    ProomPokerDesktopTopHeader,
    ProomPokerMobileTopHeader
} from 'app/components/pages/poker/ProomPokerWidgetShell';
import {WIDGET_HEADER_HEIGHT} from 'app/components/pages/poker/proom/proomWidgetGeometry';
import ProomTableWindowResizeHandle from 'app/components/pages/poker/proom/ProomTableWindowResizeHandle';
import type {
    PokerTableFrameProps,
    ProomTableCallbacks,
    ProomTableLayout,
    ProomTableMeta,
    ProomSessionContext
} from 'app/components/pages/poker/proom/proomTableTypes';
import type {ProomMessage} from 'app/utils/proom/proomMessages';
import {
    buildTableSrc,
    DESKTOP_FIT_VALUE,
    MOBILE_FIT_VALUE
} from 'app/utils/proom/proomUrl';
import {
    broadcastUserToken,
    getIframeElements,
    requestUserTokenRefresh
} from 'app/utils/proom/proomUserToken';

const TABLE_BG = '#0f0f0f';
const MOBILE_WIDGET_Z_INDEX = 50;

const noop = (): void => {
    // No-op handler
};

const noopDragStart = noop as (event: React.PointerEvent<HTMLDivElement>) => void;

const tableIframeBase: React.CSSProperties = {
    background: TABLE_BG,
    border: 'none',
    colorScheme: 'dark',
    display: 'block'
};

const styles: Record<string, React.CSSProperties> = {
    standaloneContainer: {
        background: TABLE_BG,
        inset: 0,
        position: 'fixed'
    },
    standaloneIframe: {
        ...tableIframeBase,
        height: `calc(100vh - ${WIDGET_HEADER_HEIGHT}px)`,
        width: '100%'
    },
    tableIframeDesktop: {...tableIframeBase, height: '100%', pointerEvents: 'auto', width: '100%'},
    tableIframeMobile: {
        ...tableIframeBase,
        height: `calc(100vh - ${WIDGET_HEADER_HEIGHT}px)`,
        pointerEvents: 'auto',
        width: '100%'
    },
    tableWindowDesktop: {
        background: TABLE_BG,
        boxSizing: 'border-box',
        overflow: 'hidden',
        pointerEvents: 'auto',
        position: 'fixed'
    },
    tableWindowMobileOverlay: {background: TABLE_BG, inset: 0, position: 'fixed', zIndex: MOBILE_WIDGET_Z_INDEX}
};

export type {PokerTableFrameProps} from 'app/components/pages/poker/proom/proomTableTypes';

const resolveGroupedFrameProps = ({
    session,
    table,
    layout,
    callbacks
}: {
    session: ProomSessionContext
    table: ProomTableMeta
    layout: ProomTableLayout
    callbacks: ProomTableCallbacks
}) => ({
    ...session,
    ...callbacks,
    boardId: table.boardId,
    externalId: table.externalId,
    height: layout.height,
    position: layout.position,
    tableBlinds: table.blinds ?? '',
    tableName: table.name ?? '',
    tableSrcProp: table.src,
    width: layout.width,
    zIndex: layout.zIndex
});

const PokerTableFrame = ({
    mode,
    session,
    table,
    layout = {},
    callbacks = {},
    isMobile = false,
    iframeRef: externalIframeRef,
    depositHref = getLinkPath('/deposits'),
    maximizeHref
}: PokerTableFrameProps): React.ReactElement => {
    const {
        balanceLabel,
        nickname,
        userToken,
        parentOrigin,
        playerProfilesToken,
        playerProfilesBaseUrl,
        boardId,
        externalId,
        tableName,
        tableBlinds,
        tableSrcProp,
        position,
        width,
        height,
        zIndex,
        onDragStart,
        onResizePointerDown,
        onBringToFront,
        onClose,
        onMaximize,
        onMinimize,
        onInfo,
        onLeave,
        onTableTransfer,
        onUserTokenRefreshed
    } = resolveGroupedFrameProps({callbacks, layout, session, table});
    const internalIframeRef = useRef<HTMLIFrameElement>(null);
    const iframeRef = externalIframeRef || internalIframeRef;

    const fitValue = isMobile ? MOBILE_FIT_VALUE : DESKTOP_FIT_VALUE;
    const tableSrc = tableSrcProp || buildTableSrc({
        boardId,
        externalId,
        fit: fitValue,
        lang: I18n.locale,
        nickname,
        parentOrigin,
        playerProfilesBaseUrl,
        playerProfilesToken,
        userToken
    });

    const tableNameLabel = tableName || I18n.t('poker.game');

    const handleUserTokenRequest = useCallback((): void => {
        requestUserTokenRefresh()
            .then(refreshedToken => {
                const iframes = getIframeElements([iframeRef.current]);

                broadcastUserToken(refreshedToken, iframes);
                onUserTokenRefreshed?.(refreshedToken);
            })
            .catch(() => {
                // 401 or network error: silent. Proom shows expired-session UI on next 401.
            });
    }, [iframeRef, onUserTokenRefreshed]);

    useEffect(() => {
        const handleMessage = (event: MessageEvent<ProomMessage>): void => {
            if (event.source !== iframeRef.current?.contentWindow) {
                return;
            }

            const {data} = event;

            if (data?.type === 'proom:info') {
                onInfo?.({
                    blinds: data.blinds || '',
                    currency: data.currency || '',
                    name: data.tableName || ''
                });
            }

            if (data?.type === 'proom:leave') {
                onLeave?.();
            }

            if (data?.type === 'proom:table-transfer' && data.newBoardId) {
                onTableTransfer?.(data.newBoardId);
            }

            if (data?.type === 'proom:request-user-token') {
                handleUserTokenRequest();
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [
        handleUserTokenRequest,
        iframeRef,
        onInfo,
        onLeave,
        onTableTransfer
    ]);

    if (mode === 'standalone') {
        return <div className="poker-table-standalone" style={styles.standaloneContainer}>
            <ProomPokerDesktopTopHeader
                balanceLabel={balanceLabel}
                blinds={tableBlinds}
                depositHref={depositHref}
                isDraggable={false}
                onClose={onClose ?? noop}
                onDragStart={noopDragStart}
                onMinimize={onMinimize}
                title={tableNameLabel}
            />
            <iframe
                ref={iframeRef}
                scrolling="no"
                src={tableSrc}
                style={styles.standaloneIframe}
                title="proom-table"
            />
        </div>;
    }

    if (isMobile) {
        return <div style={styles.tableWindowMobileOverlay}>
            <div className="poker-widget-mobile proom-widget-mobile-frame">
                <ProomPokerMobileTopHeader
                    balanceLabel={balanceLabel}
                    blinds={tableBlinds}
                    onClose={onClose ?? noop}
                    title={tableNameLabel}
                />
                <iframe
                    ref={iframeRef}
                    scrolling="no"
                    src={tableSrc}
                    style={styles.tableIframeMobile}
                    title="proom-table"
                />
            </div>
        </div>;
    }

    return <div
        className="poker-widget proom-widget-desktop-frame"
        onMouseDown={onBringToFront}
        style={{
            ...styles.tableWindowDesktop,
            height,
            left: position?.x,
            top: position?.y,
            width,
            zIndex
        }}
    >
        <ProomPokerDesktopTopHeader
            balanceLabel={balanceLabel}
            blinds={tableBlinds}
            depositHref={depositHref}
            maximizeHref={maximizeHref}
            onClose={onClose ?? noop}
            onDragStart={onDragStart ?? noopDragStart}
            onMaximize={onMaximize}
            title={tableNameLabel}
        />
        <div className="proom-widget-iframe-host">
            <iframe
                ref={iframeRef}
                scrolling="no"
                src={tableSrc}
                style={styles.tableIframeDesktop}
                title="proom-table"
            />
        </div>
        {onResizePointerDown && <ProomTableWindowResizeHandle onPointerDown={onResizePointerDown}/>}
    </div>;
};

export default PokerTableFrame;
